/**
 *
 * DocumentsOverviewActionBar
 *
 */

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useLocale } from 'lib/intl';
import { messages } from 'core/translations/invoiceDetails';
import { useSafeState } from '@domonda/react-plumb';
import { FormattedMessage } from 'lib/intl';
import { ResolveOnTrigger } from 'lib/ResolveOnTrigger';
import { deleteInvoice } from 'lib/api/invoice';

import {
  iconButtonClassName,
  svgIconClassName,
  createStyles,
  withStyles,
  WithStyles,
} from '@domonda/ui';
import {
  MenuItem,
  TextField,
  createMuiTheme,
  ThemeProvider,
} from '@material-ui/core';

import { Box, Flex, Dialog, Text, Button, ErrInline } from '@domonda/ui';
import { CircularProgress } from '@material-ui/core';

// icons
import { Search, ToggleIcon, HamburgerIcon } from 'lib/domonda-icons';
import { DeleteIcon, CheckmarkIcon, CheckmarkSomeIcon } from 'lib/ui/Icons';

// form
import { Form, FormInputField } from '@domonda/react-form';

// parts
import { useDocumentsOverviewQueryParams } from './queryParams';
import {
  FormControl,
  InputLabel,
  Select,
} from '@material-ui/core';
import { GetValidatableInvoiceContainersParams } from 'lib/api/invoiceContainers';
import { getConfigFromStore, getCustomerConfig } from 'lib/api/customerConfig';

const styles = createStyles(({ palette }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid transparent`,
    height: '100%',
    boxShadow: '1px 2px 4px 0px rgba(0, 0, 0, 0.2)',
    borderRadius: '40px',
    padding: '0 15px',
    margin: '16px 20px 0 0',
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    alignSelf: 'stretch',
    color: '#4A4A4A',
    height: '40px',
    [`& .${svgIconClassName}`]: {
      marginRight: '10px',
      width: '20px',
    },
  },
  searchForm: {
    flex: 1,
  },
  searchInput: {
    '& input': {
      border: 0,
      fontFamily: 'inherit',
      fontSize: 14,
      '&::placeholder': {
        color: '#4A4A4A',
        fontWeight: 400,
        fontSize: '16px',
        opacity: 1,
      },
      '&:focus': {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },

    width: '100%',
  },

  actions: {
    display: 'flex',
    padding: '6px 18px 6px 18px',
    [`& .${iconButtonClassName}`]: {
      marginRight: 8,
      '&:last-child': {
        marginRight: 0,
      },
    },
    '& + &': {
      borderLeft: `1px solid ${palette.border}`,
    },
    '&:last-child': {
      paddingRight: 0,
    },
  },

  selectBtn: {
    width: '120px',
    boxShadow: '1px 2px 4px 0px #00000033',
    borderRadius: '8px',
    fontSize: '14px !important',
    marginTop: '30px',
    textAlign: 'left',
    padding: ' 3px 0 3px 15px',

    '&:before, &:after': {
      display: 'none',
    },

    '& .MuiSelect-root': {
      paddingTop: '16px',
    },
  },

  selectLabel: {
    position: 'absolute',
    top: '8px !important',
    left: '16px !important',
    fontWeight: 700,
    color: '#4a4a4a',
    textTransform: 'uppercase',
    transition: 'none !important',
  },

  typeBtn: {
    color: '#4A4A4A',
  },

  toggleBtn: {
    width: '50px',
    height: '50px',
    margin: '10px 15px 0 0',
    cursor: 'pointer',
    transition: '0.5s ease all',

    '& rect': {
      transition: '0.5s ease all'
    },

    '&:hover':{
      backgroundColor: '#F4F7F8',
      borderRadius: '50%',
      '& rect': {
        fill: '#00AAE9',
        stroke: '#00AAE9',
      }
    }
  },

  deleteBtn: {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    transition: '0.5s ease all',
    stroke: 'transparent',
    marginLeft: 'auto',

    '&:hover':{
      '& path': {
        fill: '#00AAE9',
        stroke: '#00AAE9',
      }
    }
  },

  selectedInfo: {
    display: 'flex',
    marginLeft: '24px',
    color: '#4A4A4A',
    cursor: 'pointer',
  },

  checkBoxWrapper: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    marginRight: '8px',
    borderRadius: '4px',
    background: '#FFFFFF',
    border: '1px solid #4A4A4A',
  },

  checkBox: {
    display: 'block',

    '& svg': {
      width: '100%',
      height: '100%',
      padding: '1px',
    },

    '& rect': {
      fill: '#FFFFFF',
    }
  },

  checkBoxSelected: {
    background: '#00AAE9',
    border: '1px solid #00AAE9',

    '& span': {
      display: 'block',
    },
  },
}));

const MuiTheme = createMuiTheme({
  palette: {
    primary: {
      light: '#757ce8',
      main: '#00aae9',
      dark: '#0593e6',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ff7961',
      main: '#f44336',
      dark: '#ba000d',
      contrastText: '#000',
    },
  },
  //input label does not inherit the theme color so we have to change it manually
  overrides: {
    MuiInputLabel: {
      root: {
        '&::focus': {
          color: '#4A4A4A',
        },
      },
    },
  },
});
// function deriveStateName(documentState: DocumentsOverviewQueryParams['documentState']) {
//   switch (documentState) {
//     case 'needForAction':
//       return <FormattedMessage id="DOCUMENTS_NEED_FOR_ACTION" />;
//     case 'autoprocessed':
//       return <FormattedMessage id="DOCUMENTS_AUTOPROCESSED" />;
//     case 'error':
//       return <FormattedMessage id="DOCUMENTS_ERROR" />;
//   }
// }

export interface DocumentsOverviewActionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  classes?: WithStyles<typeof styles>['classes'];
  sizeFactor: number;
  onSizeFactorChange: (sizeFactor: number) => void;
  isColumnView: any;
  setIsColumnView: any;
  selectedDocuments: object[];
  refetchDocuments: () => void;
  setSelectedDocuments: (documents: any) => void;
  data: any;
  selectDocument: (containerId: string, invoiceId: string) => any;
}

// const SIZE_FACTOR_CHANGE = 0.2;
// const ITEM_HEIGHT = 48;
// const ITEM_PADDING_TOP = 8;
// const MenuProps = {
//   PaperProps: {
//     style: {
//       maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
//       width: 250,
//     },
//   },
// };

const DocumentsOverviewActionBar: React.FC<
  DocumentsOverviewActionBarProps & WithStyles<typeof styles>
> = (props) => {
  const { classes, className, sizeFactor, onSizeFactorChange, isColumnView, setIsColumnView, selectedDocuments, setSelectedDocuments, data, refetchDocuments, selectDocument, ...rest } = props;

  const { formatMessage } = useIntl();

  // const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [queryParams, setQueryParams] = useDocumentsOverviewQueryParams();

  // const [names, setNames] = useState<any[]>([]);

  const { locale } = useLocale();

  // const [selected, setSelected] = useState(4);

  // const onSelect = (e: React.ChangeEvent<{ value: unknown }>) => {
  //   setSelected(e.target.value as any);
  // };

  const [deleteDialog, setDeleteDialog] = useSafeState(false);

  const getTranslationObject = (key: string) => {
    if (!messages[`INVOICE_DETAIL.${key}`])
      return {
        en: key,
        de: key,
      };
    return {
      en: messages[`INVOICE_DETAIL.${key}`].en,
      de: messages[`INVOICE_DETAIL.${key}`].de
        ? messages[`INVOICE_DETAIL.${key}`].de
        : messages[`INVOICE_DETAIL.${key}`].en,
    };
  };

  const populateNames = async () => {
    let config = getConfigFromStore();
    const newNames: any[] = [];
    if (!config) config = await getCustomerConfig();
    config.InvoiceDetails.map((detail) => {
      if (
        detail.IsQueryable &&
        (detail.Priority === 'MustHave' || detail.Priority === 'NiceToHave')
      ) {
        const nameSplit = detail.Name.split('.');
        if (nameSplit.length === 1) {
          const value = getTranslationObject(nameSplit[0]);
          newNames.push({ key: detail.Name, value });
        } else if (nameSplit[0] === 'Sender' || nameSplit[0] === 'Receiver') {
          const value1 = getTranslationObject(nameSplit[0]);
          const value2 = getTranslationObject(nameSplit[nameSplit.length - 1]);
          newNames.push({
            key: detail.Name,
            value: { en: value1.en + ' ' + value2.en, de: value1.de + ' ' + value2.de },
          });
        } else {
          const value = getTranslationObject(nameSplit[nameSplit.length - 1]);
          newNames.push({ key: detail.Name, value });
        }
      }
    });
    newNames.sort((a, b) => {
      if (a.value[locale] > b.value[locale]) return 1;
      else if (a.value[locale] < b.value[locale]) return -1;
      else return 0;
    });
  };

  useEffect(() => {
    async function populate() {
      await populateNames();
    }
    populate();
  }, []);

  // const types = ['Full Text', 'Detail Based'];

  const [selectedDetails] = useState<string[]>([]);
  const [selectedTypes] = useState<string>('Full Text');

  // const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
  //   setSelectedDetails(event.target.value as any);
  // };
  // const handleTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
  //   setQueryParams({
  //     ...queryParams,
  //     fullTextQuery: null,
  //     invoiceDetailQuery: null,
  //     invoiceDetailsToQuery: null,
  //     PageNumber: 1,
  //   });
  //   setSelectedDetails([]);
  //   setSelectedTypes(event.target.value as any);
  // };

  const handleFormSubmit = (e: GetValidatableInvoiceContainersParams) => {
    if (selectedTypes === 'Detail Based') {
      setQueryParams({ ...e, invoiceDetailsToQuery: selectedDetails.join(','), PageNumber: 1 });
    } else setQueryParams({ ...e, PageNumber: 1 });
  };

  const onDeleteInvoice = async (params: any) => {
    await Promise.all(params.map((el: any) => {
      return deleteInvoice(el);
    }));
  }

  const renderSelectedInfo = (selectedDocuments: any) => {
    const canSelectDocuments = getConfigFromStore().CanDeleteInvoiceInValidation && queryParams.documentState === 'error' ? true : false
    if (canSelectDocuments) {
      switch(true) {
        // nothing selected
        case selectedDocuments.length === 0: return (
            <div
              className={classes.selectedInfo}
              onClick={() => {
                data.InvoiceContainers.map((el: any) => {
                  selectDocument(el.ContainerId, el.Invoices[0].InvoiceId);
                })
              }}
            >
              <span className={classes.checkBoxWrapper}></span>
              <span><FormattedMessage
                id="NUMBER_OF_DOCUMENTS_SHOWN"
                values={{
                  count: queryParams.PageSize,
                }}
              /></span>
            </div>
          );
          break;
        // some selected
        case selectedDocuments.length < queryParams!.PageSize!: return (
            <div
              className={classes.selectedInfo}
              onClick={() => {
                setSelectedDocuments([]);
              }}
            >
              <span className={`${classes.checkBoxWrapper} ${classes.checkBoxSelected}`}>
                <span className={classes.checkBox}><CheckmarkSomeIcon /></span>
              </span>
              <span><FormattedMessage
                id="NUMBER_OF_DOCUMENTS_SELECTED"
                values={{
                  count: selectedDocuments.length,
                }}
              /></span>
            </div>
          );
          break;
        // all selected
        case selectedDocuments.length === queryParams.PageSize: return (
            <div
              className={classes.selectedInfo}
              onClick={() => {
                setSelectedDocuments([]);
              }}
            >
              <span className={`${classes.checkBoxWrapper} ${classes.checkBoxSelected}`}>
                <span className={classes.checkBox}><CheckmarkIcon /></span>
              </span>
              <span><FormattedMessage
                id="NUMBER_OF_DOCUMENTS_SELECTED"
                values={{
                  count: selectedDocuments.length,
                }}
              /></span>
            </div>
          );
          break;
      }
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div {...rest} className={clsx(classes.root, className)}>
        <div className={classes.search}>
          <Search />
          <Form
            autoSubmit
            defaultValues={queryParams}
            className={classes.searchForm}
            onSubmit={handleFormSubmit}
          >
            <FormInputField
              path={selectedTypes === 'Full Text' ? 'fullTextQuery' : 'invoiceDetailQuery'}
            >
              {({ inputProps }) => (
                <TextField
                  {...inputProps}
                  disabled={selectedTypes.length === 0}
                  InputProps={{ disableUnderline: true }}
                  autoFocus
                  className={classes.searchInput}
                  placeholder={formatMessage({ id: 'DOCUMENTS_SEARCH_FOR_DOCUMENTS' })}
                />
              )}
            </FormInputField>
          </Form>
        </div>

       {/* <div className={classes.actions}>
          <FormControl
            style={{ minWidth: '200px', maxWidth: '200px', marginRight: '15px' }}
            error={selectedTypes.length === 0}
          >
            <ThemeProvider theme={MuiTheme}>
              <InputLabel color="primary" id="search-type">
                {formatMessage({ id: 'DOCUMENTS_SEARCH_TYPE' })}
              </InputLabel>
            </ThemeProvider>
            <ThemeProvider theme={MuiTheme}>
              <Select
                labelId="search-type"
                id="search-type-checkbox"
                value={selectedTypes}
                onChange={handleTypeChange}
                input={<Input />}
                // renderValue={(selected) => (selected as string[]).join(', ')}
                renderValue={(selected) =>
                  formatMessage({
                    id: `DOCUMENTS_${(selected as any)
                      .toString()
                      .split(' ')
                      .join('_')}`,
                  })
                }
                MenuProps={MenuProps}
                className={classes.typeBtn}
              >
                {types.map((type) => (
                  <MUIMenuItem key={type} value={type}>
                    <Checkbox color="primary" checked={selectedTypes.indexOf(type) > -1} />
                    <ListItemText
                      primary={formatMessage({ id: `DOCUMENTS_${type.split(' ').join('_')}` })}
                    />
                  </MUIMenuItem>
                ))}
              </Select>
            </ThemeProvider>
            {selectedTypes.length === 0 && (
              <FormHelperText>You must select a search type</FormHelperText>
            )}
          </FormControl>
          <FormControl style={{ minWidth: '400px', maxWidth: '400px' }}>
            <InputLabel id="demo-mutiple-checkbox-label">
              {formatMessage({ id: 'DOCUMENTS_DETAILS' })}
            </InputLabel>
            <ThemeProvider theme={MuiTheme}>
              <Select
                labelId="demo-mutiple-checkbox-label"
                id="demo-mutiple-checkbox"
                multiple
                disabled={!selectedTypes.includes('Detail Based')}
                value={selectedDetails}
                onChange={handleChange}
                input={<Input />}
                renderValue={(selected) => {
                  const n: string[] = [];
                  (selected as string[]).map((el) => {
                    names.map((name) => {
                      if (name.key === el) {
                        n.push(name.value[locale]);
                      }
                    });
                  });
                  return n.join(', ');
                }}
                MenuProps={MenuProps}
              >
                {names.map((name) => (
                  <MUIMenuItem key={name.key} value={name.key}>
                    <Checkbox color="primary" checked={selectedDetails.indexOf(name.key) > -1} />
                    <ListItemText primary={name.value[locale]} />
                  </MUIMenuItem>
                ))}
              </Select>
            </ThemeProvider>
          </FormControl>
        </div>*/}

        {/*<div className={classes.actions}>
          <Button
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white', fontFamily: 'inherit' }}
            variant="contained"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <AdjustView />
            <span>
              <FormattedMessage id="DOCUMENTS_STATE" />
              :&nbsp;{deriveStateName(queryParams.documentState)}
            </span>
          </Button>

          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem
              disabled={queryParams.documentState === 'needForAction'}
              onClick={() => {
                setQueryParams({ ...queryParams, PageNumber: 1, documentState: 'needForAction' });
                setAnchorEl(null);
              }}
            >
              {deriveStateName('needForAction')}
            </MenuItem>

            <MenuItem
              disabled={queryParams.documentState === 'autoprocessed'}
              onClick={() => {
                setQueryParams({ ...queryParams, PageNumber: 1, documentState: 'autoprocessed' });
                setAnchorEl(null);
              }}
            >
              {deriveStateName('autoprocessed')}
            </MenuItem>

            <MenuItem
              disabled={queryParams.documentState === 'error'}
              onClick={() => {
                setQueryParams({ ...queryParams, PageNumber: 1, documentState: 'error' });
                setAnchorEl(null);
              }}
            >
              {deriveStateName('error')}
            </MenuItem>
          </Menu>
        </div>*/}

{/*        <div className={classes.actions}>
          <IconButton
            className="icon"
            disabled={sizeFactor <= 1}
            onClick={() => onSizeFactorChange(sizeFactor - SIZE_FACTOR_CHANGE)}
          >
            <ZoomOutDocument />
          </IconButton>

          <IconButton
            className="icon"
            disabled={sizeFactor >= 1.6}
            onClick={() => onSizeFactorChange(sizeFactor + SIZE_FACTOR_CHANGE)}
          >
            <ZoomInDocument />
          </IconButton>
        </div>*/}
      </div>
      <div
        style={{
          textAlign: 'center',
          marginTop: '20px',
          display: 'flex',
          alignItems: 'center',
          paddingRight: '15px',
        }}
      >
        {/*<FormControl style={{ width: '200px' }}>
          <InputLabel htmlFor="grouped-select">
            {formatMessage({ id: 'DOCUMENTS_SORT_BY' })}
          </InputLabel>
          <ThemeProvider theme={MuiTheme}>
            <Select value={selected} onChange={onSelect} id="grouped-select">
              <ListSubheader>{formatMessage({ id: 'DOCUMENTS_CLIENT_NAME' })}</ListSubheader>
              <MenuItem
                onClick={() => {
                  setQueryParams({
                    ...queryParams,
                    orderBy: 'ClientName',
                    orderDescending: false,
                    PageNumber: 1,
                  });
                }}
                value={1}
              >
                A - Z
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQueryParams({
                    ...queryParams,
                    orderBy: 'ClientName',
                    orderDescending: true,
                    PageNumber: 1,
                  });
                }}
                value={2}
              >
                Z - A
              </MenuItem>
              <ListSubheader>{formatMessage({ id: 'DOCUMENTS_DATE' })}</ListSubheader>
              <MenuItem
                onClick={() => {
                  setQueryParams({
                    ...queryParams,
                    orderBy: 'TimeCreated',
                    orderDescending: false,
                    PageNumber: 1,
                  });
                }}
                value={3}
              >
                {formatMessage({ id: 'DOCUMENTS_OLDEST_FIRST' })}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQueryParams({
                    ...queryParams,
                    orderBy: 'TimeCreated',
                    orderDescending: true,
                    PageNumber: 1,
                  });
                }}
                value={4}
              >
                {formatMessage({ id: 'DOCUMENTS_NEWEST_FIRST' })}
              </MenuItem>
            </Select>
          </ThemeProvider>
        </FormControl>*/}
        {isColumnView ? <ToggleIcon className={classes.toggleBtn} onClick={()=>setIsColumnView(!isColumnView)} /> :
          <HamburgerIcon className={classes.toggleBtn} onClick={()=>setIsColumnView(!isColumnView)} />
        }
        <FormControl>
          <InputLabel htmlFor="page-size-select" className={classes.selectLabel}>
            {formatMessage({ id: 'DOCUMENTS_PAGE_SIZE' })}
          </InputLabel>
          <ThemeProvider theme={MuiTheme}>
            <Select
              id="page-size-select"
              defaultValue={25}
              className={classes.selectBtn}
            >
              <MenuItem
                onClick={() => {
                  setQueryParams({ ...queryParams, PageSize: 25, PageNumber: 1 });
                  setSelectedDocuments([]);
                }}
                value={25}
              >
                25
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQueryParams({ ...queryParams, PageSize: 50, PageNumber: 1 });
                  setSelectedDocuments([]);
                }}
                value={50}
              >
                50
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQueryParams({ ...queryParams, PageSize: 100, PageNumber: 1 });
                  setSelectedDocuments([]);
                }}
                value={100}
              >
                100
              </MenuItem>
            </Select>
          </ThemeProvider>
        </FormControl>
        {renderSelectedInfo(selectedDocuments)}
        {selectedDocuments.length > 0 && <DeleteIcon className={classes.deleteBtn} onClick={() => {setDeleteDialog(true)}} />}
        <Dialog open={deleteDialog}>
          <Box padding={2}>
            <Flex container direction="column" align="center" justify="center" spacing={1}>
              <ResolveOnTrigger
                promise={onDeleteInvoice}
                params={selectedDocuments}
                onResolve={() => {
                  setDeleteDialog(false);
                  refetchDocuments();
                  setSelectedDocuments([]);
                }}
              >
                {({ trigger, loading, error, clearError }) =>
                  loading ? (
                    <Flex align="center" autoWidth alignSelf="center" item>
                      <CircularProgress style={{ left: 0, color: '#00aae9' }} />
                    </Flex>
                  ) : (
                    <>
                      {error && (
                        <Flex item>
                          <ErrInline error={error} onClose={clearError} />
                        </Flex>
                      )}
                      <Flex item>
                        <Text>
                          <FormattedMessage id="DOCUMENTS_DELETE_DIALOG_TITLE" values={{count: selectedDocuments.length}} />
                        </Text>
                      </Flex>

                      <Flex item container justify="flex-end" spacing={1}>
                        <Flex item>
                          <Button onClick={() => setDeleteDialog(false)}>
                            <FormattedMessage id="CANCEL" />
                          </Button>
                        </Flex>

                        <Flex item>
                          <Button color="error" onClick={trigger}>
                            <FormattedMessage id="DELETE" />
                          </Button>
                        </Flex>
                      </Flex>
                    </>
                  )
                }
              </ResolveOnTrigger>
            </Flex>
          </Box>
        </Dialog>
      </div>
    </div>
  );
};

const StyledDocumentsOverviewActionBar = withStyles(styles)(DocumentsOverviewActionBar);
export { StyledDocumentsOverviewActionBar as DocumentsOverviewActionBar };
